/**
 * shortcut-search — helpers for the "/" command search in the floating Ask
 * Heidi bar.
 *
 * Typing "/" opens the shortcut menu; typing further text (e.g. "/prep")
 * filters the *entire* shortcut list (saved + all marketplace shortcuts) so the
 * user can jump straight to a match. These pure helpers build the searchable
 * set and rank matches; the menu highlights the top match and Enter runs it.
 */

import type { SuggestedChipCategory } from '@/components/ui/transcript-chips/types';
import type { PinnedChip } from './evidence-settings-popover';
import type { BrowseFilterCategory, ResolvedShortcut } from './shortcut-marketplace-data';

/** A shortcut in the searchable list, tagged with whether the user saved it. */
export interface SearchShortcut {
  id: string;
  label: string;
  query: string;
  category: SuggestedChipCategory;
  description?: string;
  browseCategory?: BrowseFilterCategory;
  /** True when this shortcut is one of the user's saved/pinned shortcuts. */
  isSaved: boolean;
  /** Present when the shortcut is team-scoped (for save analytics `is_team`). */
  teamShortcutId?: string;
}

/**
 * Build the full searchable shortcut list: saved shortcuts first (in pin
 * order), then every marketplace shortcut not already saved. De-duped by id and
 * by label so the same shortcut never appears twice.
 */
export function buildSearchableShortcuts(
  pinnedChips: PinnedChip[],
  marketShortcuts: ResolvedShortcut[]
): SearchShortcut[] {
  const seenIds = new Set<string>();
  const seenLabels = new Set<string>();
  const list: SearchShortcut[] = [];

  for (const chip of pinnedChips) {
    if (seenIds.has(chip.id)) continue;
    seenIds.add(chip.id);
    seenLabels.add(chip.label);
    list.push({
      id: chip.id,
      label: chip.label,
      query: chip.query,
      category: chip.category,
      description: chip.description,
      isSaved: true,
      ...(chip.teamShortcutId ? { teamShortcutId: chip.teamShortcutId } : {}),
    });
  }

  for (const shortcut of marketShortcuts) {
    if (seenIds.has(shortcut.id) || seenLabels.has(shortcut.label)) continue;
    seenIds.add(shortcut.id);
    seenLabels.add(shortcut.label);
    list.push({
      id: shortcut.id,
      label: shortcut.label,
      query: shortcut.query,
      category: shortcut.category,
      description: shortcut.description,
      browseCategory: shortcut.browseCategory,
      isSaved: false,
      ...(shortcut.teamShortcutId ? { teamShortcutId: shortcut.teamShortcutId } : {}),
    });
  }

  return list;
}

/** Match rank: label prefix (0) beats label substring (1) beats query hit (2). */
const matchRank = (shortcut: SearchShortcut, query: string): number => {
  const label = shortcut.label.toLowerCase();
  if (label.startsWith(query)) return 0;
  if (label.includes(query)) return 1;
  if (shortcut.query.toLowerCase().includes(query)) return 2;
  return -1;
};

/**
 * Filter + rank the searchable list against a query. An empty query returns the
 * list unchanged (browse mode). Matches are ordered by {@link matchRank}, ties
 * broken by original order so the result is stable.
 */
export function filterShortcuts(
  shortcuts: SearchShortcut[],
  rawQuery: string
): SearchShortcut[] {
  const query = rawQuery.trim().toLowerCase();
  if (!query) return shortcuts;

  return shortcuts
    .map((shortcut, index) => ({ shortcut, index, rank: matchRank(shortcut, query) }))
    .filter(({ rank }) => rank !== -1)
    .sort((a, b) => (a.rank !== b.rank ? a.rank - b.rank : a.index - b.index))
    .map(({ shortcut }) => shortcut);
}
